// ACS home = full-screen launcher (navigation refonte 21/10, spec ARBORESCENCE-ACS.md §3-4, Task 2). No dock on this route
// (shell). Order: common header (Bonjour + date, ShellActions) · Ma journée (next booked session, today's check-in, week rings;
// 3 elements max) · Launcher (4 domain tiles from the DOMAINS registry, one live status line each, → rubHref) · removable
// widgets (Mon agenda = validated variant 3, Vie au studio = photo slider), « Personnaliser » in a handle drawer (CustomSheet),
// choice remembered in localStorage under try/catch (renders the same without it) · Mes coachs (UBM Avatar → their thread).
// Full lists (planning sheet, studio sheet, news / event sheets, sleep / form check-in sheets) left the home: they live in
// their domain. Styles: hub-accueil.css (scoped .hub-acc), on top of the existing .acs-home-brand tokens.
import { Button } from "@ubm/ui/components/button";
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronRight, SlidersHorizontal } from "lucide-react";
import { StatusBar, type Route } from "./parts";
import { CustomSheet } from "./sheet";
import { Carousel } from "@ubm/ui/components/carousel";
import { Avatar } from "@ubm/ui/components/avatar";
import { Switch } from "@ubm/ui/components/switch";
import { ShellActions } from "./shell";
import { hoursBefore, useStore, weekAxes } from "./store";
import { useProgState } from "./programmations-state";
import { AgendaV3 } from "./planning";
import { DEMO_TIME, DEMO_TODAY, capitalize, longDay } from "./demo-context";
import { useAccount } from "./compte-state";
import { DAYS, STUDIO, TODAY_KEY, type Axis, type Slot } from "./home-fixtures";
import { DOMAINS, rubHref, type DomainId } from "./domains";
import { COACHES, useMessagerie } from "./messagerie-state";
import { EVENTS } from "./studio-fixtures";
import { MEAL_LABEL, PLAN, RECIPES, type MealId } from "./nutrition-fixtures";
import { AppIcon } from "./app-icons";
import { FilDuJour } from "./fil-du-jour";
import "./hub-accueil.css";

interface HomeProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

const ringTone = (i: number) => `var(--acs-axis-${i + 1})`;
const textTone = (i: number) => `var(--acs-axis-${i + 1}-text)`;
const nav = (hash: string) => { window.location.hash = hash; };

/** Concentric week rings (same geometry and tones as before), drawn at any size. */
function HomeRings({ axes, size = 118 }: { axes: Axis[]; size?: number }) {
  const radii = [52, 39, 26];
  return <svg className="cc-rings hub-acc-rings" width={size} height={size} viewBox="0 0 118 118" aria-hidden="true">
    {axes.map((a, i) => {
      const r = radii[i]!, c = 2 * Math.PI * r;
      return <g key={a.id} transform="rotate(-90 59 59)">
        <circle cx="59" cy="59" r={r} fill="none" strokeWidth="11" style={{ stroke: "var(--acs-ring-track)" }} />
        <circle cx="59" cy="59" r={r} fill="none" strokeWidth="11" strokeLinecap="round" strokeDasharray={`${c * Math.min(1, a.done / a.target)} ${c}`} style={{ stroke: ringTone(i) }} />
      </g>;
    })}
  </svg>;
}

// ---- Removable widgets: remembered per browser, never required (private mode / blocked storage → all shown). ----
type WidgetId = "agenda" | "studio";
const WIDGETS: { id: WidgetId; label: string; hint: string }[] = [
  { id: "agenda", label: "Mon agenda", hint: "Tes séances de la semaine" },
  { id: "studio", label: "Vie au studio", hint: "Actualités et événements" },
];
const KEY = "acs-accueil-widgets-masques";
const readHidden = (): WidgetId[] => {
  try { const v = JSON.parse(window.localStorage.getItem(KEY) ?? '["agenda"]'); return Array.isArray(v) ? v.filter((x): x is WidgetId => WIDGETS.some(w => w.id === x)) : []; }
  catch { return ["agenda"]; }
};
const writeHidden = (v: WidgetId[]) => { try { window.localStorage.setItem(KEY, JSON.stringify(v)); } catch { /* storage unavailable: session only */ } };

/** Meal expected next at the demo hour (planned in the Nutrition fixtures), as « Collation prévue · Yaourt… ». */
function nextMeal(): string {
  const plan = PLAN?.[TODAY_KEY];
  if (!plan) return "Aucun repas prévu";
  const h = Number(DEMO_TIME.slice(0, 2));
  const order: MealId[] = h < 10 ? ["petit-dejeuner", "dejeuner", "collation", "diner"] : h < 14 ? ["dejeuner", "collation", "diner"] : h < 17 ? ["collation", "diner"] : ["diner"];
  const id = order.find(m => plan[m]);
  if (!id) return "Rien de prévu ce soir";
  const e = plan[id]!;
  const name = "recipe" in e ? RECIPES.find(r => r.id === e.recipe)?.name : e.idea;
  return `${MEAL_LABEL[id]} prévu${id === "collation" ? "e" : ""}${name ? ` · ${name}` : ""}`;
}

export function Home(_: HomeProps) {
  const shared = useStore();
  useProgState();
  const acct = useAccount();
  const msgs = useMessagerie();
  const axes = weekAxes(shared).slice(0, 3);
  const [hidden, setHidden] = useState<WidgetId[]>(readHidden);
  const [custom, setCustom] = useState(false);
  const toggle = (id: WidgetId, on: boolean) => setHidden(h => { const next = on ? h.filter(x => x !== id) : [...h, id]; writeHidden(next); return next; });

  // Legacy deep links: ?ouvrir=studio → Studio, ?ouvrir=planning → Entraînement › Ma semaine.
  useEffect(() => {
    const m = window.location.hash.match(/ouvrir=(planning|studio)/);
    if (m) nav(m[1] === "studio" ? rubHref("studio", "actus") : rubHref("entrainement", "semaine"));
  }, []);

  const weekKeys = DAYS.map(d => d.key);
  const upcoming = useMemo(() => shared.slots
    .filter(s => s.state === "booked" && weekKeys.includes(s.day) && hoursBefore(s) > 0)
    .sort((a, b) => hoursBefore(a) - hoursBefore(b)), [shared.slots]);
  const next: Slot | undefined = upcoming[0];
  const today = shared.checkins[DEMO_TODAY];
  const checkinDone = !!today && (today.sleepMinutes !== undefined || today.form !== undefined);
  const routinesLeft = shared.habits.filter(h => h.planned.includes(DEMO_TODAY) && !h.done.includes(DEMO_TODAY)).length;
  const inLabel = (s: Slot) => { const m = Math.round(hoursBefore(s) * 60); if (m <= 0 || m >= 12 * 60) return null;
    const hh = Math.floor(m / 60), mm = m % 60; return hh ? `dans ${hh} h${mm ? ` ${String(mm).padStart(2, "0")}` : ""}` : `dans ${mm} min`; };
  const nextWhen = (s: Slot) => s.day === TODAY_KEY ? (Number(s.time.slice(0, 2)) >= 17 ? "Ce soir" : "Aujourd'hui") : capitalize(DAYS.find(d => d.key === s.day)!.long.replace(/ octobre$/, ""));

  const status: Record<DomainId, { text: string; alert?: boolean }> = {
    entrainement: { text: upcoming.length ? `${upcoming.length} séance${upcoming.length > 1 ? "s" : ""} réservée${upcoming.length > 1 ? "s" : ""}` : "Aucune séance réservée" },
    nutrition: { text: nextMeal() },
    // The check-in itself is in Ma journée: the tile tells what is left of today's routines (planned today, not ticked).
    suivi: !checkinDone ? { text: "Check-in à faire", alert: true }
      : routinesLeft ? { text: `${routinesLeft} routine${routinesLeft > 1 ? "s" : ""} à cocher aujourd'hui`, alert: true } : { text: "Routines du jour faites" },
    studio: STUDIO.news ? { text: "1 nouvelle actu", alert: true } : { text: EVENTS.length ? `${EVENTS.length} événement${EVENTS.length > 1 ? "s" : ""} à venir` : "Infos pratiques" },
  };
  const show = (id: WidgetId) => !hidden.includes(id);
  // « À faire aujourd'hui » par espace (pastille or, convention validée 21/10) : séances réservées du jour à venir, repas prévus
  // restants, check-in non envoyé + routines à cocher, nouvelle actu du studio.
  const mealsLeft = (() => { const plan = PLAN?.[TODAY_KEY]; if (!plan) return 0; const h = Number(DEMO_TIME.slice(0, 2));
    const after: MealId[] = h < 10 ? ["petit-dejeuner", "dejeuner", "collation", "diner"] : h < 14 ? ["dejeuner", "collation", "diner"] : h < 17 ? ["collation", "diner"] : ["diner"];
    return after.filter(m => plan[m]).length > 0 ? 1 : 0; })();
  const todo: Record<DomainId, number> = {
    entrainement: upcoming.filter(x => x.day === TODAY_KEY).length,
    nutrition: mealsLeft,
    suivi: (checkinDone ? 0 : 1) + routinesLeft,
    studio: STUDIO.news ? 1 : 0,
  };

  return <div className="cc-screen cc-home acs-home-brand acs-home-rupture hub-acc">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-today">
        <div><p className="cc-overline">{capitalize(longDay(DEMO_TODAY))}</p><h1 className="cc-title hub-acc-hello">Bonjour {acct.firstName}</h1></div>
        <ShellActions />
      </header>

      {/* Ma journée: ONE surface, the next booked session is the primary action; check-in and rings share the foot row. */}
      <section className="hub-acc-day" aria-labelledby="t-journee">
        <h2 id="t-journee" className="hub-acc-h">Ma journée</h2>
        <FilDuJour />
      </section>

      {/* Launcher: the 4 domains of the registry, each with one live line; opens the domain on its remembered rubrique. */}
      {/* Le sprite des icônes d'app est rendu une fois par le dock unique (dock.tsx). */}
      <section aria-labelledby="t-espaces">
        <div className="cc-sectionhead"><h2 id="t-espaces">Mes espaces</h2></div>
        <nav className="hub-acc-apps" aria-label="Mes espaces">
          {DOMAINS.map(d => { const n = todo[d.id]; return <button key={d.id} type="button" className="hub-acc-app" onClick={() => nav(rubHref(d.id))}
            aria-label={`${d.label}${n ? `, ${n} à faire aujourd'hui` : ""}`}>
            <span className="hub-acc-app-ic"><AppIcon domain={d.id} />{n > 0 && <span className="hub-acc-app-badge" aria-hidden="true">{n > 9 ? "9+" : n}</span>}</span>
            <span className="hub-acc-app-nm">{d.label}</span>
          </button>; })}
        </nav>
      </section>

      {show("agenda") && <AgendaV3 view="mine" origin="accueil" headingId="mon-agenda" heading="Mon agenda"
        foot={<button type="button" className="cc-seeall" onClick={() => nav(rubHref("entrainement", "semaine"))}>Ma semaine <ChevronRight size={13} strokeWidth={2.2} /></button>} />}

      {show("studio") && (STUDIO.news || STUDIO.event) && <section className="hp-studio" aria-labelledby="vie-studio">
        <div className="cc-sectionhead"><h2 id="vie-studio">Vie au studio</h2><button type="button" className="cc-seeall" onClick={() => nav(rubHref("studio", "actus"))}>Tout voir <ChevronRight size={13} strokeWidth={2.2} /></button></div>
        <div className="hp-carousel"><Carousel label="Vie au studio" slideSize="90%" slideLabel={(i, n) => `${i === 0 && STUDIO.news ? "Actualité" : "Événement"}, ${i + 1} sur ${n}`}>
          {[
            STUDIO.news && <button key="news" type="button" className="hp-story" onClick={() => nav(rubHref("studio", "actus"))}>
              <img src={STUDIO.news.photo} alt="" />
              <span className="hp-story-meta">Actualité · {STUDIO.news.date}</span>
              <span className="hp-story-title">{STUDIO.news.title}</span>
            </button>,
            STUDIO.event && <button key="event" type="button" className="hp-story" onClick={() => nav(rubHref("studio", "actus"))}>
              <img src={STUDIO.event.photo} alt="" />
              <span className="hp-story-meta">Événement · {STUDIO.event.date} · {STUDIO.event.place}</span>
              <span className="hp-story-title">{STUDIO.event.title}</span>
            </button>,
          ].filter(Boolean)}
        </Carousel></div>
      </section>}

      {/* Mes coachs: real names and portraits (studio-fixtures), each opens its thread. */}
      <section className="hub-acc-coaches" aria-labelledby="t-coachs">
        <div className="cc-sectionhead"><h2 id="t-coachs">Mes coachs</h2></div>
        <ul className="hub-acc-coachlist">
          {COACHES.map((c, i) => { const n = msgs.unread[c.id] ?? 0; return <li key={c.id}>
            <button type="button" className="hub-acc-coachrow" onClick={() => nav(`#/messages?fil=${c.id}`)} aria-label={`Écrire à ${c.name}${n ? `, ${n} message non lu` : ""}`}>
              <Avatar name={c.name} src={c.photo} size="md" />
              <span className="hub-acc-coachrow-t"><b>{c.name.split(" ")[0]}</b><small>{i === 0 ? `Ton coach${n ? ` · ${n} nouveau${n > 1 ? "x" : ""} message${n > 1 ? "s" : ""}` : ""}` : c.role}</small></span>
              {n > 0 ? <span className="hub-acc-coachrow-n" aria-hidden="true">{n > 9 ? "9+" : n}</span> : <ChevronRight className="hub-acc-chev" size={16} strokeWidth={2} aria-hidden="true" />}
            </button>
          </li>; })}
        </ul>
      </section>

      <div className="hub-acc-foot">
        <Button variant="ghost" size="sm" onClick={() => setCustom(true)}><SlidersHorizontal size={15} strokeWidth={2} aria-hidden="true" />Personnaliser l'accueil</Button>
        <button type="button" className="cc-home-demo cc-home-onb-link" onClick={() => nav("#/onboarding")}>Première connexion · exemple</button>
      </div>
    </div>

    {custom && <CustomSheet title="Personnaliser l'accueil" onClose={() => setCustom(false)} overlayClassName="cc-home-overlay" className="cc-home-sheet hub-acc-sheet">
      <p className="cc-home-p">Choisis ce que tu veux voir sous tes espaces. Ma journée et tes coachs restent toujours là.</p>
      <ul className="hub-acc-wlist">
        {WIDGETS.map(w => <li key={w.id}>
          <span><b>{w.label}</b><small>{w.hint}</small></span>
          <Switch checked={show(w.id)} onCheckedChange={on => toggle(w.id, on)} aria-label={`Afficher ${w.label}`} />
        </li>)}
      </ul>
    </CustomSheet>}
  </div>;
}
